import {
  ArrowDownTrayIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import date from "date-and-time";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IInvoice, InvoiceVatQualification } from "../../models/invoice/IInvoice";
import StatusPill, { StatusPillColour } from "../shared/StatusPill";
import LoadingWheel from "../shared/LoadingWheel";
import useInvoices from "../../data/useInvoices";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import SelectInput from "../shared/Inputs/SelectInput";
import { InvoiceStatus } from "../../models/Invoice";
import { DateTimeInput } from "../shared/Inputs/DateTimeInput";
import { SearchAccount } from "../accounts/SearchAccount";
import PagedTable, { DateRangeType } from "../shared/Tables/PagedTable";
import { createColumnHelper, RowModel, Table } from "@tanstack/react-table";
import IndeterminateCheckbox from "../shared/Tables/IndeterminateCheckbox";
import TableCheckboxCol from "../shared/Tables/IndeterminateCheckbox";
import useApiHelper from "../../hooks/useApiHelper";
import apiConfig from "../../utils/apiConfig";
import PriceFormat from "../../utils/priceFormat";
import useInvoiceMutations from "../../data/useInvoiceMutations";
import useNotification from "../notifications/useNotifications";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";

interface InvoiceRowProps {
  invoice: IInvoice;
  onClick: (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => void;
}

interface InvoiceListProps {
  status?: string;
  accountId?: string;
  partId?: string;
  invoiceDate?: DateRangeType;
  hideFilters?: boolean;
}

function DownloadInvoiceButton({
  invoiceId,
  download,
  onError,
}: {
  invoiceId: string;
  download: (id: string) => Promise<void>;
  onError: () => void;
}) {
  const [downloading, setDownloading] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDownloading(true);
    download(invoiceId)
      .catch(onError)
      .finally(() => setDownloading(false));
  };

  return (
    <div className="px-3 flex flex-col items-center justify-center">
      {downloading ? (
        <svg
          role="status"
          className="w-5 h-5 text-gray-300 animate-spin fill-primary-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
      ) : (
        <ArrowDownTrayIcon
          className="w-5 h-5 text-gray-600 hover:text-primary transition-colors duration-200 cursor-pointer"
          onClick={handleClick}
        />
      )}
    </div>
  );
}

export default function InvoiceList(filters: InvoiceListProps) {
  const { t } = useTranslation("accounts");
  const { handleSubmit, register, setValue, watch } = useForm({
    defaultValues: {
      status: filters.status ?? undefined,
      accountId: filters.accountId ?? undefined,
      partId: filters.partId ?? undefined,
      invoiceDate: filters.invoiceDate ?? { fieldName: "invoiceDate", start: null, end: null },
      vatQualification: undefined as InvoiceVatQualification | undefined,
    }
  });
  const { post } = useApiHelper(apiConfig("InvoiceList"));

  const status = watch("status");
  const accountId = watch("accountId");
  const partId = watch("partId");
  const start = watch("invoiceDate.start");
  const end = watch("invoiceDate.end");
  const dateRange = watch("invoiceDate") as DateRangeType;
  const vatQualification = watch("vatQualification");


  const trueFilters = useMemo(() => ({
    status: filters.status ?? status,
    accountId: filters.accountId ?? accountId,
    partId: filters.partId ?? partId,
    invoiceDate: filters.invoiceDate ?? (start && end ? dateRange : undefined),
    vatQualification,
  }), [filters, status, accountId, partId, start, end, dateRange, vatQualification]);


  const { downloadInvoice, downloadBulkInvoices, downloadInvoicePdfs } = useInvoiceMutations();
  const { addNotification } = useNotification();
  const hasTme = CheckFeatureFlag("TME");

  const notifyDownloadError = () =>
    addNotification({
      variant: "error",
      primaryText: t("Failed to download invoice"),
    });

  // The zip still downloads when some invoices fail to render, so say so - otherwise a zip
  // that is quietly short a few invoices looks exactly like a complete one.
  const notifyPartialPdfDownload = (failed: number) => {
    if (failed <= 0) return;

    addNotification({
      variant: "warn",
      primaryText:
        failed === 1
          ? t("{{count}} invoice could not be generated", { count: failed })
          : t("{{count}} invoices could not be generated", { count: failed }),
      secondaryText: t("The rest are in the zip. Try those invoices again individually."),
    });
  };

  const notifyPdfDownloadError = (error: Error) =>
    addNotification({
      variant: "error",
      primaryText: t("Failed to download invoices"),
      secondaryText: error?.message,
    });

  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [oracleDownloading, setOracleDownloading] = useState(false);
  const [pdfDownloadingSelected, setPdfDownloadingSelected] = useState(false);
  const [pdfDownloadingAll, setPdfDownloadingAll] = useState(false);

  const navigate = useNavigate();

  const colHelper = createColumnHelper<IInvoice>();

  const cols = useMemo(
    () => [
      TableCheckboxCol(false),
      colHelper.accessor("invoiceNumber", {
        header: t("Invoice No"),
        sortingFn: "auto",
      }),
      colHelper.accessor("account.name", {
        header: t("Account Name"),
        sortingFn: "auto",
      }),
      colHelper.accessor("invoiceDate", {
        header: t("Invoice Date"),
        sortingFn: "auto",
        cell: (info) => <span>{info.getValue() !== null ? date.format(new Date(info.getValue()!), "DD/MM/YYYY") : t("Not Set")}</span>,
      }),
      colHelper.accessor("reference", {
        header: t("Reference"),
        sortingFn: "auto",
      }),
      colHelper.accessor("isGlobalSale", {
        header: t("Scheme"),
        enableSorting: false,
        cell: (info) => {
          const invoice = info.row.original;
          if (invoice.isGlobalSale)
            return <StatusPill text={t("GLOBAL")} colour={StatusPillColour.Blue} />;
          if (invoice.items?.some((i) => i.isMarginScheme))
            return <StatusPill text={t("MARGIN")} colour={StatusPillColour.Orange} />;
          if (invoice.isGlobalSale === false)
            return <StatusPill text={t("VAT")} colour={StatusPillColour.Green} />;
          return null;
        },
      }),
      colHelper.accessor("dueDate", {
        header: t("Due Date"),
        sortingFn: "auto",
        cell: (info) => <span>{info.getValue() !== null ? date.format(new Date(info.getValue()!), "DD/MM/YYYY") : t("Not Set")}</span>,
      }),
      colHelper.accessor("invoiceTotal", {
        header: t("Total"),
        sortingFn: "auto",
        cell: (info) => <span>{info.getValue() != null ? PriceFormat(info.getValue()) : t("Not Set")}</span>,
      }),
      colHelper.accessor("status", {
        header: t("Status"),
        sortingFn: "auto",
        cell: (info) => <span
          className={`px-3 py-1 rounded-full font-bold ${{
            0: "bg-blue-100 text-blue-600",
            1: "bg-amber-100 text-amber-600",
            2: "bg-green-100 text-green-600",
            3: "bg-red-100 text-red-600",
          }[info.getValue()]
            }`}
        >
          {
            {
              0: t("Draft"),
              1: t("Approved"),
              2: t("Paid"),
              3: t("Credit"),
            }[info.getValue()]
          }
        </span>,
      }),
      colHelper.display({
        id: "actions",
        header: t("Actions"),
        cell: (info) => (
          <DownloadInvoiceButton
            invoiceId={info.row.original.id}
            download={downloadInvoice}
            onError={notifyDownloadError}
          />
        ),
      }),
    ],
    [t]
  );

  const onSubmit = (data: any) => {
    console.log(data);
  };

  // var table = undefined;

  // if (invoices.isLoading) {
  //   table = (
  //     <div className="flex items-center justify-center py-10">
  //       <LoadingWheel />
  //     </div>
  //   );
  // } else if (
  //   invoices.isError ||
  //   !invoices.data ||
  //   !invoices.data.data
  // ) {
  //   table = (
  //     <div className="flex items-center justify-center py-10">
  //       <p className="text-red-600 font-bold text-xl">Error loading invoices</p>
  //     </div>
  //   );
  // } else {
  //   table = (

  //   );
  // }


  return <>
    {/* ---------- Filter ---------- */}
    <form onSubmit={handleSubmit(onSubmit)} className="mt-2 mx-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {filters.accountId == undefined && (
        <div className="sm:col-span-2 lg:col-span-4">
          <SearchAccount
            label={t("Account")}
            onAccountSelect={(account) => setValue("accountId", account?.id)}
          />
        </div>
      )}

      {filters.status == undefined && (
        <SelectInput
          label={t("Status")}
          options={[
            { id: undefined, name: t("All") },
            { id: InvoiceStatus.Draft, name: t("Draft") },
            { id: InvoiceStatus.Approved, name: t("Approved") },
            { id: InvoiceStatus.Paid, name: t("Paid") },
            { id: InvoiceStatus.Credit, name: t("Credit") },
          ]}
          register={register}
          registerName="status"
          setValue={setValue}
          watch={watch}
        />
      )}
      <SelectInput
        label={t("VAT qualification")}
        options={[
          { id: undefined, name: t("All") },
          { id: InvoiceVatQualification.Vat, name: t("VAT") },
          { id: InvoiceVatQualification.Margin, name: t("Margin") },
          { id: InvoiceVatQualification.Global, name: t("Global") },
        ]}
        register={register}
        registerName="vatQualification"
        setValue={setValue}
        watch={watch}
      />
      {filters.invoiceDate?.start == undefined && (
        <DateTimeInput
          label={t("From")}
          register={register}
          registerName="invoiceDate.start"
        />
      )}
      {filters.invoiceDate?.end == undefined && (
        <DateTimeInput
          label={t("To")}
          register={register}
          registerName="invoiceDate.end"
        />
      )}

    </form>
    <hr className="text-gray-300 mt-8 col-span-3 mx-4" />
    {/* ---------------------------- */}
    <div className="p-4">
      <div className="sm:hidden p-2">
        <p className="text-center">{t("Tap a row to see the full record")}</p>
      </div>

      <PagedTable
        url="/invoice/filtered"
        columns={cols}
        queryLabel={t("Search part details")}
        onRowClick={(x: IInvoice) => {
          navigate(`/invoices/${x.id}`);
        }}
        showTotal={true}
        additionalBody={trueFilters}
        actionButtons={[
          {
            label: t("Download Selected"),
            onClick: (rows: RowModel<IInvoice>) => {
              setPdfDownloadingSelected(true);
              downloadInvoicePdfs({ ids: rows.rows.map((row) => row.original.id) })
                .then(notifyPartialPdfDownload)
                .catch(notifyPdfDownloadError)
                .finally(() => setPdfDownloadingSelected(false));
            },
            disabledOnNoRows: true,
            isLoading: pdfDownloadingSelected,
          },
          {
            label: t("Download All Pages"),
            onClick: (_rows: RowModel<IInvoice>, _table: Table<IInvoice>, query?: string) => {
              setPdfDownloadingAll(true);
              downloadInvoicePdfs({ filter: trueFilters, query })
                .then(notifyPartialPdfDownload)
                .catch(notifyPdfDownloadError)
                .finally(() => setPdfDownloadingAll(false));
            },
            isLoading: pdfDownloadingAll,
          },
          {
            label: t("Download Xero"),
            onClick: (rows: RowModel<IInvoice>, table: Table<IInvoice>) => {
              setBulkDownloading(true);
              downloadBulkInvoices(rows.rows.map((row: any) => row.original.id), "xero")
                .catch(() =>
                  addNotification({
                    variant: "error",
                    primaryText: t("Failed to download invoices"),
                  })
                )
                .finally(() => setBulkDownloading(false));
            },
            disabledOnNoRows: true,
            isLoading: bulkDownloading,
          },
          ...(hasTme
            ? [
                {
                  label: t("Download Oracle"),
                  onClick: (rows: RowModel<IInvoice>, table: Table<IInvoice>) => {
                    setOracleDownloading(true);
                    downloadBulkInvoices(rows.rows.map((row: any) => row.original.id), "oracle")
                      .catch((error: Error) =>
                        addNotification({
                          variant: "error",
                          primaryText: t("Failed to download invoices"),
                          // The export refuses with the account or setting that needs fixing - showing it
                          // is the difference between an actionable error and a dead end.
                          secondaryText: error?.message,
                        })
                      )
                      .finally(() => setOracleDownloading(false));
                  },
                  disabledOnNoRows: true,
                  isLoading: oracleDownloading,
                },
              ]
            : []),
        ]}
      />
    </div>
  </>
}
